import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useChat } from '../context/ChatContext';
import { useTier } from '../context/TierContext';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent } from './ui/dialog';
import { UpgradePrompt } from './FeatureGate';
import {
  ChatToggleButton,
  ChatHeader,
  ChatRoomList,
  ChatMessageList,
  ChatInput,
  CreateRoomDialog
} from './chat';

const ChatPanelRefactored = () => {
  const location = useLocation();
  const { user, api } = useAuth();
  const { hasFeature } = useTier();
  const canUseChat = hasFeature('messaging');
  
  const isOnChatPage = location.pathname.startsWith('/chat');
  const isOnOnboardingPage = location.pathname === '/onboarding' || 
                             location.pathname === '/free-trial' || 
                             location.pathname === '/verify-email';
  const {
    rooms,
    activeRoom,
    messages,
    isOpen,
    loading,
    unreadCount,
    openRoom,
    closeChat,
    sendMessage,
    createRoom,
    setIsOpen,
  } = useChat();

  const [showNewRoom, setShowNewRoom] = useState(false);
  const [timeFormat, setTimeFormat] = useState('24h');

  useEffect(() => {
    if (user?.client_id) {
      api.get(`/tenants/${user.client_id}`).then(res => {
        if (res.data?.time_format) setTimeFormat(res.data.time_format);
      }).catch(() => {});
    }
  }, [user?.client_id]);

  const handleCreateRoom = async (name, type, jobId, participantIds) => {
    const room = await createRoom(name, type, jobId, participantIds);
    if (room) {
      openRoom(room);
    }
    return room;
  };

  // Don't render on dedicated chat page or onboarding pages
  if (isOnChatPage || isOnOnboardingPage) {
    return null;
  }

  // Upgrade prompt for locked feature
  if (!canUseChat) {
    return (
      <>
        <ChatToggleButton canUseChat={canUseChat} setIsOpen={setIsOpen} unreadCount={unreadCount} />
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogContent className="sm:max-w-md">
            <UpgradePrompt
              feature="messaging"
              title="Chat & Messaging"
              description="Real-time chat between office and job site is available on Advanced and Professional plans."
            />
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <>
      <ChatToggleButton canUseChat={canUseChat} setIsOpen={setIsOpen} unreadCount={unreadCount} />

      {/* Chat Panel Slide-out */}
      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-96 bg-background border-l shadow-xl z-50 transform transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <ChatHeader
          activeRoom={activeRoom}
          openRoom={openRoom}
          closeChat={closeChat}
          setShowNewRoom={setShowNewRoom}
        />

        {activeRoom ? (
          <div className="flex flex-col h-[calc(100vh-3.5rem)]">
            <ChatMessageList 
              messages={messages} 
              loading={loading} 
              userId={user?.id}
              timeFormat={timeFormat}
            />
            <ChatInput onSend={sendMessage} />
          </div>
        ) : (
          <ChatRoomList 
            rooms={rooms} 
            openRoom={openRoom} 
            setShowNewRoom={setShowNewRoom} 
          />
        )}
      </div>

      <CreateRoomDialog
        open={showNewRoom}
        onOpenChange={setShowNewRoom}
        api={api}
        onCreateRoom={handleCreateRoom}
      />

      {/* Backdrop for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 sm:hidden"
          onClick={closeChat}
        />
      )}
    </>
  );
};

export default ChatPanelRefactored;
